/** Permission string constants, mirroring the API's permission catalogue. */
export const Perm = {
  Dashboard: { View: 'Dashboard.View' },
  /** Opening the embedded Superset dashboards; the guest token is scoped to the person's organisation. */
  Analytics: { View: 'Analytics.View' },
  Strategy: { View: 'Strategy.View', Manage: 'Strategy.Manage' },
  Kpis: { View: 'Kpis.View', Capture: 'Kpis.Capture', Review: 'Kpis.Review', Approve: 'Kpis.Approve' },
  Initiatives: { View: 'Initiatives.View', Manage: 'Initiatives.Manage' },
  Evidence: { View: 'Evidence.View', Upload: 'Evidence.Upload', Manage: 'Evidence.Manage' },
  /** Electronic signatures (docs §219). Signing one's own step needs no permission: being the named recipient is enough. */
  Signing: { View: 'Signing.View', Request: 'Signing.Request', Manage: 'Signing.Manage' },
  /** Document management (docs §246 on); the names match DocumentsPermissions on the server. */
  Documents: { Capture: 'Documents.Capture', Records: 'Documents.Records', Compliance: 'Documents.Compliance', Admin: 'Documents.Admin' },
  // Health (docs §279): the door and the running of the module; what a person does comes from their roles at each facility.
  Health: { Use: 'Health.Use', Administer: 'Health.Administer', ApproveConfiguration: 'Health.ApproveConfiguration', Audit: 'Health.Audit', Oversight: 'Health.Oversight' },
  // Learning (docs §261): everyone learns; these run, author, teach, manage, report on and audit learning.
  Learning: {
    ViewRecords: 'Learning.ViewRecords', Assign: 'Learning.Assign', Approve: 'Learning.Approve', ManageUsers: 'Learning.ManageUsers', Author: 'Learning.Author',
    Publish: 'Learning.Publish', ManageSessions: 'Learning.ManageSessions', Reports: 'Learning.Reports', Configure: 'Learning.Configure', Organisation: 'Learning.Organisation',
  },
  /** Lending (docs §222 on): the loan management system; the names match LendingPermissions on the server. */
  Lending: {
    View: 'Lending.View',
    Configure: 'Lending.Configure',
    Products: 'Lending.Products',
    ApproveProducts: 'Lending.ApproveProducts',
    Customers: 'Lending.Customers',
    ViewSensitive: 'Lending.ViewSensitive',
    ApproveKyc: 'Lending.ApproveKyc',
    Originate: 'Lending.Originate',
    Assess: 'Lending.Assess',
    Approve: 'Lending.Approve',
    Committee: 'Lending.Committee',
    Disburse: 'Lending.Disburse',
    AuthoriseDisbursement: 'Lending.AuthoriseDisbursement',
    PostPayments: 'Lending.PostPayments',
    ReversePayments: 'Lending.ReversePayments',
    RunEod: 'Lending.RunEod',
    Collections: 'Lending.Collections',
    Recoveries: 'Lending.Recoveries',
    WriteOff: 'Lending.WriteOff',
    ApproveWriteOff: 'Lending.ApproveWriteOff',
    Finance: 'Lending.Finance',
    ValidateModels: 'Lending.ValidateModels',
    PrepareReturns: 'Lending.PrepareReturns',
    ReviewReturns: 'Lending.ReviewReturns',
    ApproveReturns: 'Lending.ApproveReturns',
    AmlView: 'Lending.AmlView',
    AmlInvestigate: 'Lending.AmlInvestigate',
    VerifyIdentity: 'Lending.VerifyIdentity',
    Complaints: 'Lending.Complaints',
    CreditReporting: 'Lending.CreditReporting',
    Privacy: 'Lending.Privacy',
    FieldAgent: 'Lending.FieldAgent',
    Borrower: 'Lending.Borrower',
    Collector: 'Lending.Collector',
    Reports: 'Lending.Reports',
    Audit: 'Lending.Audit',
    ConfirmRules: 'Lending.ConfirmRules',
  },
  Reports: { View: 'Reports.View', Generate: 'Reports.Generate' },
  Audit: { View: 'Audit.View' },
  /** The platform's data-protection registers (docs §235). */
  Privacy: { View: 'Privacy.View', Manage: 'Privacy.Manage' },
  Users: { View: 'Users.View', Manage: 'Users.Manage' },
  Roles: { View: 'Roles.View', Manage: 'Roles.Manage' },
  Settings: { Manage: 'Settings.Manage' },
  Platform: { Manage: 'Platform.Manage' },
  Governance: { View: 'Governance.View', Manage: 'Governance.Manage' },
  BoardPacks: { View: 'BoardPacks.View', Manage: 'BoardPacks.Manage', Publish: 'BoardPacks.Publish', Distribute: 'BoardPacks.Distribute' },
  /** Asking the platform to draft narrative. Accepting a draft needs BoardPacks.Manage as well. */
  Intelligence: { Draft: 'Intelligence.Draft' },
  Decisions: { View: 'Decisions.View', Manage: 'Decisions.Manage' },
  Actions: { View: 'Actions.View', Manage: 'Actions.Manage' },
  Risks: { View: 'Risks.View', Manage: 'Risks.Manage', Configure: 'Risks.Configure' },
  /** The accounting module; useless where the organisation has the module off, because its endpoints are gated by the module first. */
  Accounting: {
    View: 'Accounting.View', Configure: 'Accounting.Configure', Capture: 'Accounting.Capture',
    Post: 'Accounting.Post', ClosePeriod: 'Accounting.ClosePeriod', ReopenPeriod: 'Accounting.ReopenPeriod',
    ControlOverride: 'Accounting.ControlOverride',
    CapexRequest: 'Accounting.CapexRequest',
    CapexApprove: 'Accounting.CapexApprove',
  },
  /** The stock and inventory module; gated by the module before any of these apply. */
  Stock: {
    View: 'Stock.View', Configure: 'Stock.Configure', Request: 'Stock.Request', Receive: 'Stock.Receive', Issue: 'Stock.Issue',
    Transfer: 'Stock.Transfer', Count: 'Stock.Count', Adjust: 'Stock.Adjust', Approve: 'Stock.Approve', ClosePeriod: 'Stock.ClosePeriod',
    Override: 'Stock.Override',
  },
  /** The HR and payroll module. ViewPay is the confidentiality boundary; self-service needs no permission. */
  Hr: {
    View: 'Hr.View', Configure: 'Hr.Configure', ManageEmployees: 'Hr.ManageEmployees', ViewSensitive: 'Hr.ViewSensitive',
    Leave: 'Hr.Leave', Attendance: 'Hr.Attendance', ViewPay: 'Hr.ViewPay', RunPayroll: 'Hr.RunPayroll',
    ApprovePayroll: 'Hr.ApprovePayroll', Recruit: 'Hr.Recruit', PublishVacancy: 'Hr.PublishVacancy',
    Approve: 'Hr.Approve', Override: 'Hr.Override', Reports: 'Hr.Reports',
  },
  Fleet: {
    View: 'Fleet.View', Configure: 'Fleet.Configure', ManageVehicles: 'Fleet.ManageVehicles', RequestTrip: 'Fleet.RequestTrip',
    Dispatch: 'Fleet.Dispatch', Fuel: 'Fleet.Fuel', Workshop: 'Fleet.Workshop', Approve: 'Fleet.Approve', Drive: 'Fleet.Drive',
    Reports: 'Fleet.Reports', ViewLocations: 'Fleet.ViewLocations',
  },
  Parking: {
    View: 'Parking.View', Configure: 'Parking.Configure', ManageFacilities: 'Parking.ManageFacilities', ManageTariffs: 'Parking.ManageTariffs',
    ManageVehicles: 'Parking.ManageVehicles', OperateSessions: 'Parking.OperateSessions', CollectPayments: 'Parking.CollectPayments',
    ProcessRefunds: 'Parking.ProcessRefunds', Waive: 'Parking.Waive', ManagePermits: 'Parking.ManagePermits', OperateGates: 'Parking.OperateGates',
    Override: 'Parking.Override', Approve: 'Parking.Approve', Reports: 'Parking.Reports', Integrations: 'Parking.Integrations', ViewImages: 'Parking.ViewImages',
  },
  Property: {
    View: 'Property.View', Configure: 'Property.Configure', ManageRegister: 'Property.ManageRegister', Leasing: 'Property.Leasing', ApproveLeasing: 'Property.ApproveLeasing',
    Billing: 'Property.Billing', Maintenance: 'Property.Maintenance', PlanWork: 'Property.PlanWork', FieldWork: 'Property.FieldWork', Compliance: 'Property.Compliance',
    Owner: 'Property.Owner', Occupier: 'Property.Occupier', Contractor: 'Property.Contractor', Reports: 'Property.Reports', Audit: 'Property.Audit',
  },
  Budgeting: {
    View: 'Budgeting.View', Configure: 'Budgeting.Configure', Prepare: 'Budgeting.Prepare', Review: 'Budgeting.Review', Consolidate: 'Budgeting.Consolidate',
    Approve: 'Budgeting.Approve', ControllingOfficer: 'Budgeting.ControllingOfficer', BudgetOffice: 'Budgeting.BudgetOffice', Treasury: 'Budgeting.Treasury',
    Release: 'Budgeting.Release', RequestChanges: 'Budgeting.RequestChanges', Appropriate: 'Budgeting.Appropriate', Reports: 'Budgeting.Reports', Audit: 'Budgeting.Audit',
  },
} as const;
